import React, { ChangeEvent, ClipboardEvent, forwardRef, ForwardRefRenderFunction, InputHTMLAttributes, useCallback, useImperativeHandle, useRef } from 'react';

import { Input, InputRef } from '../../../input';

export type SegmentChangeEvent = (props: { index: number; maxLength: number; nativeEvent: ChangeEvent<HTMLInputElement> }) => void;

export type SegmentPasteEvent = (props: { index: number; maxLength: number; nativeEvent: ClipboardEvent<HTMLInputElement> }) => void;

export type SegmentProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onPaste' | 'onFocus'> & {
  index: number;
  value: string;
  onChange: SegmentChangeEvent;
  onPaste: SegmentPasteEvent;
  maxLength: number;
  onFocus?: () => void;
};

export type SegmentRef = { focus: () => void };

const SegmentBase: ForwardRefRenderFunction<{ focus: () => void }, SegmentProps> = ({ index, maxLength, value, onChange, onPaste, onFocus, ...props }, ref) => {
  const inputRef = useRef<InputRef>(null);
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
  }));

  const handleOnChange = useCallback<(props: ChangeEvent<HTMLInputElement>) => void>(
    (nativeEvent) => {
      onChange({ index, maxLength, nativeEvent });
    },
    [index, maxLength, onChange]
  );

  const handleOnPaste = useCallback<(props: ClipboardEvent<HTMLInputElement>) => void>(
    (nativeEvent) => {
      onPaste({ index, maxLength, nativeEvent });
    },
    [index, maxLength, onPaste]
  );

  return (
    <Input
      autoComplete={`input-segment-${index}-${Date.now()}`}
      ref={inputRef}
      value={value}
      type='text'
      onChange={handleOnChange}
      onPaste={handleOnPaste}
      onFocus={onFocus}
      {...props}
    />
  );
};

export const Segment = forwardRef(SegmentBase);
